import React from 'react';
import { connect } from 'react-redux';
import { Col, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/pro-light-svg-icons';
import LeftSide from '../main/left-side';
import Navbar from '../navigation/navbar';
import VerticalNavigation from '../navigation/vertical-navigation';
import LargeCard from '../main/large-card';
import Postit from '../main/postit';
import RightSide from '../main/right-side';
import downloadsGermany from './data/downloadsGermany.json';
import { RootState } from '../../store';
import useTranslate from '../useTranslate';

interface Props {
    selectedPartner: {
        country_id: number;
    };
}

interface SimpleFileItem {
    name: string;
    language: string;
}

interface RawFileItem {
    filename: string;
    eng: SimpleFileItem;
    ger: SimpleFileItem;
}

interface FileItem extends SimpleFileItem {
    fileName: string;
}

interface RawCollections {
    userInformation: RawFileItem[];
    laundryRoomInformation: RawFileItem[];
    partnerBoardInstructions: RawFileItem[];
}

function chooseListsByCountry(countryId: number): RawCollections {
    return downloadsGermany;
}

function chooseByLanguage(list: RawFileItem[], language: string): FileItem[] {
    return list.map((val) => {
        const languageSpecificItems: SimpleFileItem = val[language] ?? val.ger;

        return {
            fileName: val.filename,
            ...languageSpecificItems,
        };
    });
}

function chooseListsByCountryAndLanguage(countryId: number, language: string) {
    const lists = chooseListsByCountry(countryId);

    return {
        userInformation: chooseByLanguage(lists.userInformation, language),
        laundryRoomInformation: chooseByLanguage(
            lists.laundryRoomInformation,
            language
        ),
        partnerBoardInstructions: chooseByLanguage(
            lists.partnerBoardInstructions,
            language
        ),
    };
}

function Downloads(props: Readonly<Props>) {
    const strings = useTranslate('Downloads');

    const createRow = (file: FileItem) => {
        return (
            <tr>
                <td>{file.name}</td>
                <td>{file.language}</td>
                <td>
                    <a
                        href={`/static/${file.fileName}`}
                        target="_blank"
                        className="pull-right"
                    >
                        <FontAwesomeIcon
                            icon={faDownload}
                            className="color-blue"
                        />
                    </a>
                </td>
            </tr>
        );
    };

    const selectedPartner = props.selectedPartner;
    if (!selectedPartner) {
        return null;
    }

    const {
        userInformation,
        laundryRoomInformation,
        partnerBoardInstructions,
    } = chooseListsByCountryAndLanguage(
        selectedPartner.country_id,
        strings._currentLanguage
    );

    return (
        <Navbar nav>
            <Row>
                <LeftSide>
                    <VerticalNavigation selected="downloads" />{' '}
                </LeftSide>

                <Col xs={6} className={'col-middle'}>
                    <LargeCard type="card-edit" title={strings.userInformation}>
                        <table className="data-table" style={{ width: '100%' }}>
                            <thead>
                                <tr>
                                    <th>{strings.name}</th>
                                    <th>{strings.language}</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {userInformation.map((file) => createRow(file))}
                            </tbody>
                        </table>
                    </LargeCard>

                    <LargeCard
                        type="card-edit"
                        title={strings.laundryRoomInformation}
                    >
                        <table className="data-table" style={{ width: '100%' }}>
                            <thead>
                                <tr>
                                    <th>{strings.name}</th>
                                    <th>{strings.language}</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {laundryRoomInformation.map((file) =>
                                    createRow(file)
                                )}
                            </tbody>
                        </table>
                    </LargeCard>

                    <LargeCard
                        type="card-edit"
                        title={strings.partnerBoardInstructions}
                    >
                        <table className="data-table" style={{ width: '100%' }}>
                            <thead>
                                <tr>
                                    <th>{strings.name}</th>
                                    <th>{strings.language}</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {partnerBoardInstructions.map((file) =>
                                    createRow(file)
                                )}
                            </tbody>
                        </table>
                    </LargeCard>
                </Col>

                <RightSide>
                    <Postit>
                        {strings.postIt.text1}
                        <br />
                        <br />
                        {strings.postIt.text2}
                    </Postit>
                </RightSide>
            </Row>
        </Navbar>
    );
}

export default connect((store: RootState) => ({
    selectedPartner: store.partnerManager.selectedPartner,
}))(Downloads);
